/* istanbul ignore file */
import { SectionRow, BigSectionRow } from 'framework/ui/layout';
import { BooleanField, DateField, EnumField, TextField } from 'framework/ui/field';
import type { Data } from 'framework/types';
import ContractPositionField from './field/ContractPositionField';
import ContractEndDateField from './field/ContractEndDateField';
import ContractAssignmentTypeField from './field/ContractAssignmentTypeField';
import ContractWorkRateField from './field/ContractWorkRateField';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import {
  ContractTypeField,
  EmployeeOfficeField,
  EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS,
  EMPLOYEE_CONTRACT_TYPES_ALIASES,
  CONTRACTING_PARTY_FORMS,
} from 'domain/shared/employee';
import { isPolishOfficeForm } from 'domain/people/employee/contracts/createupdate/EmployeeContractForm';
import PaidLeaveBalanceField from './field/PaidLeaveBalanceField';
import useContractsErrorMap from 'domain/people/employee/contracts/errorHandlers';
import PreviousPaidLeaveBalanceField from 'domain/people/contracts/shared/field/PreviousPaidLeaveBalanceField';
import { isEmployeeContract } from 'domain/people/employee/contracts/shared/employeeContractsService';
import { isBusinessRegistrationCountryVisible } from 'domain/people/employee/contracts/shared/contractFieldVisibility';
import ContractDurationField from 'domain/people/contracts/shared/field/ContractDurationField';
import { BusinessRulesListField } from 'domain/people/shared/formRules';
import BusinessRulesViolatedField from 'domain/people/leave/shared/input/BusinessRulesViolatedField';
import type { Contract } from 'domain/people/employee/contracts/types';

type ContractViewData = Data<Contract>;

const isPreviousPaidLeaveBalanceExist = ({ previousPaidLeaveBalance }: Pick<Contract, 'previousPaidLeaveBalance'>) => {
  return previousPaidLeaveBalance !== null;
};

const ContractsViewFields = () => {
  return (
    <>
      <SectionRow>
        <EmployeeOfficeField source="officeId" label="Registered office" />
        <TextField source="number" label="Contract number" />
        <ContractPositionField source="contractPosition" label="Contract position" />
        <ContractTypeField source="contractType" label="Contract type" />
        <ContractDurationField dateFromSource="startDate" dateToSource="endDate" label="Contract duration" />
        <PaidLeaveBalanceField source="paidLeaveBalance" label="Paid Leave balance" isVisible={isPolishOfficeForm} />
        <PreviousPaidLeaveBalanceField
          source="previousPaidLeaveBalance"
          label="Start Paid Leave Balance"
          tooltip="Paid Leave Balance for the first calendar year"
          isVisible={(data: ContractViewData) =>
            isPolishOfficeForm(data) && isEmployeeContract(data) && isPreviousPaidLeaveBalanceExist(data.getData())
          }
        />
        <DateField source="dateOfSignature" label="Date of signature" />
        <DateField source="startDate" label="Contract start date" />
        <ContractEndDateField source="endDate" label="Contract end date" />
        <ContractAssignmentTypeField source="assignmentType" label="Assignment type" />
        <ContractWorkRateField
          source="workRate"
          label="Work Rate"
          isVisible={(data: ContractViewData) =>
            data.getValueBySource('assignmentType') === EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS.PART_TIME
          }
        />
        <EnumField
          source="contractingPartyForm"
          label="Contracting party form"
          isVisible={(data: ContractViewData) =>
            data.getValueBySource('contractType') === EMPLOYEE_CONTRACT_TYPES_ALIASES.CFS
          }
          options={CONTRACTING_PARTY_FORMS}
        />

        <TextField
          source="businessRegistrationCountry"
          label="Business registration country"
          isVisible={(data: ContractViewData) => isBusinessRegistrationCountryVisible(data.getData())}
        />
        <BooleanField source="hasProbationPeriod" label="Probation period" />
        <DateField
          source="probationPeriodEndedAt"
          label="Probation period end date"
          isVisible={(data: ContractViewData) => data.getValueBySource('hasProbationPeriod')}
        />
      </SectionRow>
      <SectionRow>
        <BusinessRulesViolatedField source="violatedBusinessRules" label="Business rules violated" />
        <BusinessRulesListField
          source="violatedBusinessRules"
          label="Violated business rules"
          isVisible={(data: ContractViewData) =>
            !!data.getValueBySource('violatedBusinessRules') && !!data.getValueBySource('violatedBusinessRules').length
          }
          errorMap={useContractsErrorMap(true)}
        />
        <TextField source="comment" label="Rules violation reason" />
        <BooleanField source="secondary" />
      </SectionRow>
      <BigSectionRow>
        <AttachmentsListField source="scanIds" label="Contract scans" />
      </BigSectionRow>
    </>
  );
};

export default ContractsViewFields;
